import React from "react";
import { Link } from "react-router-dom";
import LegalLayout, { H2, P, CALLOUT } from "../components/LegalLayout";
import { ContractIllustration } from "../components/legal/LegalIllustrations";

const SECTIONS = [
  { id: "purpose", labelFr: "Objet du service", labelEn: "Purpose of the service" },
  { id: "eligibility", labelFr: "Éligibilité", labelEn: "Eligibility" },
  { id: "account", labelFr: "Compte utilisateur", labelEn: "User account" },
  { id: "conduct", labelFr: "Règles de conduite", labelEn: "Rules of conduct" },
  { id: "content", labelFr: "Contenus publiés", labelEn: "User content" },
  { id: "activities", labelFr: "Activités et rencontres", labelEn: "Activities and meetups" },
  { id: "ratings", labelFr: "Évaluations et badges", labelEn: "Ratings and badges" },
  { id: "payments", labelFr: "Paiements", labelEn: "Payments" },
  { id: "termination", labelFr: "Suspension et résiliation", labelEn: "Suspension and termination" },
  { id: "liability", labelFr: "Responsabilité", labelEn: "Liability" },
  { id: "changes", labelFr: "Modifications", labelEn: "Changes" },
  { id: "law", labelFr: "Droit applicable", labelEn: "Governing law" },
];

export default function Terms() {
  return (
    <LegalLayout
      titleFr="Conditions Générales d'Utilisation" titleEn="Terms of Use"
      taglineFr="Les règles que tu acceptes en créant un compte OLTODO."
      taglineEn="The rules you agree to by creating an OLTODO account."
      updatedAt="11 septembre 2026"
      illustration={<ContractIllustration />}
      sections={SECTIONS}
      tldrFr={[
        "OLTODO relie ses membres autour d'activités, de services entre particuliers et de communautés, il n'organise pas ces activités lui-même.",
        "Tu dois avoir 18 ans ou plus, un compte est personnel et ne se partage pas.",
        "Le détail de ce qui est autorisé ou non se trouve dans les Règles de la communauté, celui des paiements dans la page Paiements et commissions.",
        "Tu peux supprimer ton compte à tout moment, OLTODO peut suspendre le tien en cas de manquement grave.",
      ]}
      tldrEn={[
        "OLTODO connects members around activities, peer-to-peer services and communities, it doesn't organize these activities itself.",
        "You must be 18 or older, an account is personal and can't be shared.",
        "The detail of what's allowed or not lives in the Community Rules, payment detail in the Payments and Commissions page.",
        "You can delete your account at any time, OLTODO can suspend yours for serious misconduct.",
      ]}
    >
      {(lang) => lang === "fr" ? (
        <>
          <P>
            Les présentes Conditions Générales d'Utilisation (« CGU ») régissent l'accès et l'utilisation de
            l'application et du site OLTODO, édités par SAS LNV. En créant un compte, tu
            acceptes sans réserve les présentes CGU, ainsi que les <Link to="/community-rules" style={{ color: "#0a0a0a", fontWeight: 700 }}>Règles de la communauté</Link> et
            la page <Link to="/payments-policy" style={{ color: "#0a0a0a", fontWeight: 700 }}>Paiements et commissions</Link>, qui en font partie intégrante.
          </P>

          <H2 id="purpose">1. Objet du service</H2>
          <P>OLTODO est une application sociale permettant à ses membres de créer, découvrir et rejoindre des
          activités (« Passions »), de proposer ou solliciter des services entre particuliers (« Powers »), de
          rejoindre des communautés et des rituels, d'échanger avec d'autres membres, et de partager des trajets
          (« Move »).</P>

          <H2 id="eligibility">2. Éligibilité</H2>
          <P>L'inscription est réservée aux personnes âgées d'au moins 18 ans. En créant un compte, tu garantis
          avoir l'âge requis et fournir des informations exactes. Une vérification du compte (un selfie ; une pièce
          justificative reste facultative) est requise pour débloquer toute interaction avec d'autres membres, voir les
          {" "}<Link to="/community-rules" style={{ color: "#0a0a0a", fontWeight: 700 }}>Règles de la communauté</Link>.</P>

          <H2 id="account">3. Compte utilisateur</H2>
          <P>Tu es responsable de la confidentialité de tes identifiants et de toute activité effectuée depuis ton
          compte. Un compte est personnel et ne peut être cédé ni partagé.</P>

          <H2 id="conduct">4. Règles de conduite</H2>
          <P>
            En utilisant OLTODO, tu t'engages à respecter les <Link to="/community-rules" style={{ color: "#0a0a0a", fontWeight: 700 }}>Règles de la communauté</Link>,
            qui détaillent précisément ce qui est autorisé, ce qui ne l'est pas, comment fonctionnent le blocage,
            le masquage et le signalement, ainsi que les conséquences possibles en cas de manquement.
          </P>

          <H2 id="content">5. Contenus publiés par les utilisateurs</H2>
          <P>Tu restes propriétaire des contenus que tu publies. En les publiant, tu accordes à OLTODO une
          licence mondiale, non exclusive et gratuite pour les héberger, afficher, reproduire et distribuer dans
          le cadre du fonctionnement du service. Tu garantis détenir les droits nécessaires sur tout contenu que
          tu publies.</P>

          <H2 id="activities">6. Activités et rencontres</H2>
          <P>Les activités publiées sur OLTODO sont organisées par les membres eux-mêmes. OLTODO fournit l'outil
          de mise en relation mais n'est pas partie à ces activités et n'en garantit ni la tenue, ni la qualité,
          ni la sécurité. Chaque membre participe sous sa propre responsabilité, voir la section correspondante
          des <Link to="/community-rules#activities" style={{ color: "#0a0a0a", fontWeight: 700 }}>Règles de la communauté</Link>.</P>

          <H2 id="ratings">7. Évaluations et badges</H2>
          <P>Après une activité, les participants peuvent s'évaluer mutuellement. Ces évaluations et les badges qui
          en découlent reflètent l'expérience déclarée des membres, OLTODO ne garantit pas leur exactitude mais se
          réserve le droit de retirer une évaluation manifestement abusive.</P>

          <H2 id="payments">8. Paiements et commission</H2>
          <P>
            Certaines fonctionnalités (réservation de lieux, prestations Power, articles de catalogue, trajets
            Move) peuvent donner lieu à un paiement, traité par notre prestataire Stripe. OLTODO applique une
            commission de 10&nbsp;% hors taxes sur les transactions concernées, plus la TVA applicable.
          </P>
          <P>
            OLTODO agit comme intermédiaire transparent&nbsp;: le vendeur (lieu ou prestataire) facture le
            client pour la prestation, et OLTODO ne lui facture que sa commission. En renseignant ton identité
            de facturation dans Réglages → Paiements, tu mandates OLTODO pour émettre en ton nom et pour ton
            compte les reçus destinés à tes clients. Toutes les factures sont numérotées et téléchargeables
            depuis Réglages → Paiements → Mes factures.
          </P>
          <CALLOUT>
            Le détail complet, séquestre, délais, signalement, virements, remboursements, se trouve sur la page{" "}
            <Link to="/payments-policy" style={{ color: "#0a0a0a", fontWeight: 700 }}>Paiements et commissions</Link>.
          </CALLOUT>

          <H2 id="termination">9. Suspension et résiliation</H2>
          <P>Tu peux supprimer ton compte à tout moment depuis les réglages. OLTODO peut suspendre ou supprimer un
          compte en cas de violation des présentes CGU ou des Règles de la communauté, sans préavis en cas de
          manquement grave.</P>

          <H2 id="liability">10. Limitation de responsabilité</H2>
          <P>OLTODO fournit le service « en l'état ». Dans les limites permises par la loi, OLTODO ne saurait être
          tenu responsable des dommages indirects résultant de l'utilisation du service ou des interactions entre
          membres.</P>

          <H2 id="changes">11. Modification des CGU</H2>
          <P>OLTODO peut modifier les présentes CGU, toute modification substantielle sera notifiée dans
          l'application avant son entrée en vigueur.</P>

          <H2 id="law">12. Droit applicable</H2>
          <P>Les présentes CGU sont soumises au droit français.</P>
        </>
      ) : (
        <>
          <P>
            These Terms of Use (“Terms”) govern access to and use of the OLTODO application and website,
            published by SAS LNV. By creating an account, you accept these Terms in full, as
            well as the <Link to="/community-rules" style={{ color: "#0a0a0a", fontWeight: 700 }}>Community Rules</Link> and
            the <Link to="/payments-policy" style={{ color: "#0a0a0a", fontWeight: 700 }}>Payments and Commissions</Link> page, which form an integral part of them.
          </P>

          <H2 id="purpose">1. Purpose of the Service</H2>
          <P>OLTODO is a social application that lets members create, discover and join activities (“Passions”),
          offer or request peer-to-peer services (“Powers”), join communities and rituals, connect with other
          members, and share rides (“Move”).</P>

          <H2 id="eligibility">2. Eligibility</H2>
          <P>Registration is reserved for individuals aged 18 or older. By creating an account, you warrant that
          you meet this age requirement and that the information you provide is accurate. Account verification
          (a selfie; a supporting document is optional) is required to unlock any interaction with other members, see the
          {" "}<Link to="/community-rules" style={{ color: "#0a0a0a", fontWeight: 700 }}>Community Rules</Link>.</P>

          <H2 id="account">3. User Account</H2>
          <P>You are responsible for keeping your credentials confidential and for all activity on your account.
          An account is personal and may not be transferred or shared.</P>

          <H2 id="conduct">4. Rules of Conduct</H2>
          <P>
            By using OLTODO, you agree to follow the <Link to="/community-rules" style={{ color: "#0a0a0a", fontWeight: 700 }}>Community Rules</Link>,
            which detail exactly what's allowed, what isn't, how blocking, hiding and reporting work, and the
            possible consequences for a violation.
          </P>

          <H2 id="content">5. User-Generated Content</H2>
          <P>You retain ownership of the content you post. By posting it, you grant OLTODO a worldwide,
          non-exclusive, royalty-free license to host, display, reproduce and distribute it as necessary to
          operate the service. You warrant that you hold the necessary rights to any content you post.</P>

          <H2 id="activities">6. Activities and Meetups</H2>
          <P>Activities posted on OLTODO are organized by members themselves. OLTODO provides the matching tool
          but is not a party to these activities and does not guarantee they will take place, nor their quality
          or safety. Each member participates at their own responsibility, see the corresponding section of the
          {" "}<Link to="/community-rules#activities" style={{ color: "#0a0a0a", fontWeight: 700 }}>Community Rules</Link>.</P>

          <H2 id="ratings">7. Ratings and Badges</H2>
          <P>After an activity, participants may rate one another. These ratings, and the badges derived from
          them, reflect members' self-reported experience, OLTODO does not guarantee their accuracy but reserves
          the right to remove a rating that is manifestly abusive.</P>

          <H2 id="payments">8. Payments and Commission</H2>
          <P>
            Certain features (venue bookings, Power services, catalog items, Move rides) may involve payment,
            processed by our provider Stripe. OLTODO applies a 10% commission (excluding tax) on relevant
            transactions, plus applicable VAT.
          </P>
          <P>
            OLTODO acts as a transparent intermediary: the seller (venue or provider) invoices the client for
            the service, and OLTODO only invoices the seller for its commission. By entering your billing
            identity in Settings → Payments, you mandate OLTODO to issue client receipts in your name and on
            your behalf. All invoices are numbered and downloadable from Settings → Payments → My invoices.
          </P>
          <CALLOUT>
            The full detail, escrow, timelines, reporting, payouts, refunds, is on the{" "}
            <Link to="/payments-policy" style={{ color: "#0a0a0a", fontWeight: 700 }}>Payments and Commissions</Link> page.
          </CALLOUT>

          <H2 id="termination">9. Suspension and Termination</H2>
          <P>You may delete your account at any time from the settings. OLTODO may suspend or delete an account
          in case of breach of these Terms or the Community Rules, without notice in case of serious misconduct.</P>

          <H2 id="liability">10. Limitation of Liability</H2>
          <P>OLTODO provides the service “as is.” To the extent permitted by law, OLTODO shall not be liable for
          indirect damages resulting from use of the service or interactions between members.</P>

          <H2 id="changes">11. Changes to the Terms</H2>
          <P>OLTODO may amend these Terms, any material change will be notified within the app before taking
          effect.</P>

          <H2 id="law">12. Governing Law</H2>
          <P>These Terms are governed by French law.</P>
        </>
      )}
    </LegalLayout>
  );
}
